/**
 * Sleep Score Algorithm & Calculations
 * Revised scoring: Duration(30) + Circadian(25) + Consistency(25) + Subjective(10) + Factor Bonus(10) = 100
 */

// Convert any time-ish input to minutes from midnight.
// Accepts: Date, "HH:MM" / "HH:MM:SS" 24h string, or full ISO timestamp.
export function toMinutesFromMidnight(input) {
  if (input == null) return 0;
  if (typeof input === 'string') {
    const m = input.match(/^(\d{1,2}):(\d{2})/);
    if (m) return Number(m[1]) * 60 + Number(m[2]);
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? 0 : d.getHours() * 60 + d.getMinutes();
  }
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? 0 : input.getHours() * 60 + input.getMinutes();
  }
  return 0;
}

// Convert minutes to time string (HH:MM)
export function minutesToTimeString(minutes) {
  if (minutes < 0) minutes += 1440;
  if (minutes >= 1440) minutes -= 1440;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m.toString().padStart(2, '0')} ${period}`;
}

// Circular standard deviation for time-of-day data
function circularStdDev(minutesArray) {
  const n = minutesArray.length;
  if (n < 2) return 0;
  const angles = minutesArray.map(m => (m / 1440) * 2 * Math.PI);
  const sinSum = angles.reduce((s, a) => s + Math.sin(a), 0) / n;
  const cosSum = angles.reduce((s, a) => s + Math.cos(a), 0) / n;
  const R = Math.sqrt(sinSum * sinSum + cosSum * cosSum);
  if (R >= 1) return 0;
  const variance = -2 * Math.log(R);
  return Math.sqrt(variance) * (1440 / (2 * Math.PI));
}

// Duration Score (0-30) - Gaussian curve
export function scoreDuration(durationMinutes, goalMinutes = 480) {
  const sigma = 60;
  const deviation = durationMinutes - goalMinutes;
  const raw = Math.exp(-0.5 * Math.pow(deviation / sigma, 2));

  let penalty = 1.0;
  if (durationMinutes < 300) {
    penalty = durationMinutes / 300;
  }
  if (durationMinutes > 600) {
    penalty = Math.max(0.5, 1 - (durationMinutes - 600) / 300);
  }

  return Math.round(raw * penalty * 30);
}

// Circadian Score (0-25) - alignment with target bedtime
export function scoreCircadian(bedtime, targetBedtime) {
  const bedMin = toMinutesFromMidnight(bedtime);
  const targetMin = toMinutesFromMidnight(targetBedtime || '22:30');

  let deviation = Math.abs(bedMin - targetMin);
  if (deviation > 720) deviation = 1440 - deviation;

  const baseScore = Math.exp(-0.5 * Math.pow(deviation / 45, 2)) * 20;

  // Late night penalty (after 1 AM)
  let latePenalty = 0;
  if (bedMin < 300 && bedMin > 0) {
    latePenalty = Math.min(5, bedMin > 60 ? (bedMin - 60) / 48 : 0);
  }

  // Morning wake bonus
  const wakeMin = toMinutesFromMidnight(new Date());
  let morningBonus = 0;
  if (wakeMin >= 360 && wakeMin <= 540) morningBonus = 5;
  else if (wakeMin >= 300 && wakeMin <= 600) morningBonus = 3;

  return Math.round(Math.max(0, Math.min(25, baseScore - latePenalty + morningBonus)));
}

// Consistency Score (0-25) - based on recent entries
export function scoreConsistency(recentEntries) {
  if (!recentEntries || recentEntries.length < 3) return 15;

  const bedtimes = recentEntries.map(e => toMinutesFromMidnight(e.bedtime));
  const waketimes = recentEntries.map(e => toMinutesFromMidnight(e.wakeTime));

  const bedtimeStdDev = circularStdDev(bedtimes);
  const waketimeStdDev = circularStdDev(waketimes);
  const weightedStdDev = waketimeStdDev * 0.6 + bedtimeStdDev * 0.4;

  const score = 25 * Math.exp(-0.5 * Math.pow(weightedStdDev / 40, 2));
  return Math.round(score);
}

// Subjective Score (0-10) - from quality rating
export function scoreSubjective(qualityRating) {
  return Math.round((qualityRating - 1) * 2.5);
}

// Factor Bonus (0-10) - from lifestyle factors
export function scoreFactors(factors) {
  if (!factors) return 5;
  let bonus = 5;

  if (factors.exercise) bonus += Math.min(2, (factors.exerciseMinutes || 30) / 30);
  if (factors.caffeine) bonus -= 2;
  if (factors.screenTime > 60) bonus -= Math.min(2, (factors.screenTime - 60) / 60);
  if (factors.alcohol) bonus -= 2;
  if (factors.stress > 3) bonus -= Math.min(2, (factors.stress - 3));

  return Math.round(Math.max(0, Math.min(10, bonus)));
}

// Get weakest area for actionable insight
function getWeakestArea(breakdown) {
  const normalized = {
    duration: breakdown.duration / 30,
    circadian: breakdown.circadian / 25,
    consistency: breakdown.consistency / 25,
  };
  // Imported nights (Apple Health) carry no subjective rating — leave it out
  // so a missing rating can never read as the weakest area.
  if (breakdown.subjective != null) {
    normalized.subjective = breakdown.subjective / 10;
  }
  const entries = Object.entries(normalized);
  entries.sort(([, a], [, b]) => a - b);
  return entries[0][0];
}

const AREA_LABELS = {
  duration: 'Sleep Duration',
  circadian: 'Bedtime Timing',
  consistency: 'Schedule Consistency',
  subjective: 'Sleep Quality',
};

const AREA_TIPS = {
  duration: 'Try going to bed 30 minutes earlier tonight.',
  circadian: 'Aim to be in bed within 30 minutes of your target bedtime.',
  consistency: 'Keep your bedtime and wake time consistent, even on weekends.',
  subjective: 'Try a wind-down routine: dim lights, no screens, calm activity.',
};

export const TIP_SOURCE = {
  label: 'NHS — How to get to sleep',
  url: 'https://www.nhs.uk/live-well/sleep-and-tiredness/how-to-get-to-sleep/',
};

// Complete sleep score calculation
export function calculateSleepScore(sleep, recentEntries = [], factors = null, goalMinutes = 480) {
  const duration = scoreDuration(sleep.durationMinutes, goalMinutes);
  const circadian = scoreCircadian(sleep.bedtime, sleep.targetBedtime);
  const consistency = scoreConsistency(recentEntries);

  // Imported nights (Apple Health) have no subjective rating. Rather than
  // faking a rating or penalising the night with a zero Quality component,
  // drop subjective entirely and renormalise the remaining 90 points to /100.
  // The breakdown records subjective: null + subjectiveOmitted so the UI can
  // hide the Quality bar instead of showing a misleading value. Manual logs
  // (qualityRating always set by the form) score exactly as before.
  if (sleep.qualityRating == null) {
    const factorBonusOnly = scoreFactors(factors);
    const raw = duration + circadian + consistency + factorBonusOnly;
    const total = Math.min(100, Math.round((raw / 90) * 100));
    const weakest = getWeakestArea({ duration, circadian, consistency });
    return {
      total,
      breakdown: { duration, circadian, consistency, subjective: null, subjectiveOmitted: true, factorBonus: factorBonusOnly },
      weakestArea: weakest,
      weakestLabel: AREA_LABELS[weakest],
      tip: AREA_TIPS[weakest],
      grade: total >= 85 ? 'Excellent' : total >= 70 ? 'Good' : total >= 55 ? 'Fair' : total >= 40 ? 'Poor' : 'Needs Attention',
    };
  }

  const subjective = scoreSubjective(sleep.qualityRating || 3);
  const factorBonus = scoreFactors(factors);

  const total = Math.min(100, duration + circadian + consistency + subjective + factorBonus);
  const weakest = getWeakestArea({ duration, circadian, consistency, subjective });

  return {
    total,
    breakdown: { duration, circadian, consistency, subjective, factorBonus },
    weakestArea: weakest,
    weakestLabel: AREA_LABELS[weakest],
    tip: AREA_TIPS[weakest],
    grade: total >= 85 ? 'Excellent' : total >= 70 ? 'Good' : total >= 55 ? 'Fair' : total >= 40 ? 'Poor' : 'Needs Attention',
  };
}

// Sleep debt calculation with exponential decay
export function calculateSleepDebt(sleepEntries, goalMinutes = 480) {
  const DECAY_RATE = 0.85;
  const RECOVERY_EFFICIENCY = 0.33;
  const MAX_DAYS = 14;

  const sorted = [...sleepEntries]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, MAX_DAYS);

  let totalDebt = 0;

  for (let i = 0; i < sorted.length; i++) {
    const deficit = goalMinutes - sorted[i].durationMinutes;
    const effectiveDeficit = deficit > 0 ? deficit : deficit * RECOVERY_EFFICIENCY;
    totalDebt += effectiveDeficit * Math.pow(DECAY_RATE, i);
  }

  totalDebt = Math.max(0, totalDebt);
  const debtHours = +(totalDebt / 60).toFixed(1);

  let severity, color, message;
  if (totalDebt <= 30) {
    severity = 'minimal'; color = 'var(--color-score-excellent)'; message = 'Sleep debt is negligible.';
  } else if (totalDebt <= 120) {
    severity = 'mild'; color = 'var(--color-score-good)'; message = 'Slight deficit. An extra 30 min tonight would help.';
  } else if (totalDebt <= 300) {
    severity = 'moderate'; color = 'var(--color-warning)'; message = 'Notable debt. Prioritize 1-2 early nights this week.';
  } else if (totalDebt <= 480) {
    severity = 'significant'; color = 'var(--color-score-poor)'; message = 'Significant debt affecting cognition.';
  } else {
    severity = 'severe'; color = 'var(--color-danger)'; message = 'Severe debt. Expect reduced performance.';
  }

  return { totalDebtMinutes: Math.round(totalDebt), totalDebtHours: debtHours, severity, color, message };
}

// Calculate duration from bedtime and wake time. Handles both ISO timestamps
// and plain "HH:MM" strings (assumes wake is the morning after bed).
export function calculateDuration(bedtime, wakeTime) {
  const bed = new Date(bedtime);
  const wake = new Date(wakeTime);
  if (!Number.isNaN(bed.getTime()) && !Number.isNaN(wake.getTime())) {
    return Math.round((wake - bed) / 60000);
  }
  const bedMin = toMinutesFromMidnight(bedtime);
  const wakeMin = toMinutesFromMidnight(wakeTime);
  // If wake is earlier in the day than bed, it's the next morning
  const diff = wakeMin >= bedMin ? wakeMin - bedMin : 1440 - bedMin + wakeMin;
  return diff;
}

// Format duration in hours and minutes
export function formatDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

// Get score color based on value
export function getScoreColor(score) {
  if (score >= 85) return 'var(--color-score-excellent)';
  if (score >= 70) return 'var(--color-score-good)';
  if (score >= 55) return 'var(--color-warning)';
  if (score >= 40) return 'var(--color-score-poor)';
  return 'var(--color-danger)';
}

// Generate date string (YYYY-MM-DD)
export function toDateString(date) {
  return date.toISOString().split('T')[0];
}

// Get last N dates as strings
export function getLastNDates(n) {
  const dates = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.push(toDateString(d));
  }
  return dates;
}
